import React, { useState, useEffect, useRef } from 'react';
import { Backdrop, Grid, Container, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Link,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { Button, BoxV2 as Box, Flex, Table, useTable } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faSquareMinus,
  faRotateRight,
  faBullhorn,
  faTag,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faSquareMinus as faSquareMinusSolid,
  faSquareCheck as faSquareCheckSolid,
} from '@fortawesome/pro-solid-svg-icons';

import {
  SuspensionModal,
  MatchedCampaignRow,
  MatchedBrandRow,
} from '../components';
import Spinner from '../../Campaigns/components/Spinner';
import { Loader } from '../../../../shared_elements';
import { MNO_STATUS_ENUM } from '../../../../constants';
import { toastFlashMessage } from '../../../../utils';
import {
  getSuspensionRule,
  getSuspensionPreviewCampaigns,
  getSuspensionPreviewBrands,
  createSuspensionRule,
  updateSuspensionRule,
} from '../../Brands/apiServices';

const useStyles = makeStyles(() => ({
  root: {
    padding: 0,
    '& .top-blk .button-wrapper': {
      justifyContent: 'flex-end',
    },
  },
  backLink: {
    color: '#C4C8C9',
    '& img': {
      marginRight: '3px',
    },
  },
  title: {
    textAlign: 'center',
    marginBottom: '40px',
    '& .heading1': {
      fontSize: '18px',
      lineHeight: '21px',
      marginBottom: '16px',
      wordBreak: 'break-word',
    },
    '& .heading2': {
      fontWeight: 400,
      '& img': {
        marginRight: '3px',
        verticalAlign: 'top',
        height: '14px',
      },
      '& img + span': {
        fontWeight: 700,
      },
    },
  },
  tabs: {
    display: 'flex',
    '& .tab-label': {
      border: '1px solid #C0CCD4',
      width: '225px',
      height: '26px',
      padding: '10px 18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontFamily: 'Roboto',
      fontSize: '16px',
      fontWeight: 500,
      color: '#49535D',
      backgroundColor: '#E5F0F4',
      cursor: 'pointer',
      '& img': {
        height: '18px',
        marginRight: '5px',
        filter: 'grayscale(1)',
      },
      '&.active': {
        color: '#00698F',
        fontWeight: 800,
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #FFFFFF',
        position: 'relative',
      },
      '&.active img': {
        filter: 'grayscale(0)',
      },
      '& + .tab-label': {
        borderLeft: 0,
      },
    },
  },
  tabContents: {
    marginTop: '-1px',
    border: '1px solid #C0CCD4',
    padding: '15px',
    overflowX: 'auto',
  },
  progressBackground: {
    background: 'rgba(14, 31, 43, 0.75)',
    zIndex: 1005,
  },
  progressModal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '430px',
    height: '226px',
    background: '#FFFFFF',
    borderRadius: '5px',
    marginLeft: 'calc(50% - 120px)',
  },
  progressText: {
    textAlign: 'center',
    width: '100%',
    marginTop: 30,
    color: '#49535D',
    wordBreak: 'break-all',
  },
}));

const CampaignHeadRows = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'brandName', label: 'BRAND DBA NAME', sortable: false },
  { id: 'brand.csp.displayName', label: 'CSP DBA NAME', sortable: false },
  { id: 'usecase', label: 'USE-CASE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'operationStatus', label: 'MNO STATUS', sortable: false },
];
const BrandHeadRows = [
  { id: 'uid', label: 'BRAND ID', sortable: true },
  { id: 'displayName', label: 'BRAND DBA NAME', sortable: true },
  { id: 'csp.displayName', label: 'CSP DBA NAME', sortable: false },
  { id: 'entityType', label: 'ENTITY TYPE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
];
const TabName = {
  CAMPAIGN: 'campaigns',
  BRAND: 'brands',
};

const LIST_API = {
  [TabName.CAMPAIGN]: getSuspensionPreviewCampaigns,
  [TabName.BRAND]: getSuspensionPreviewBrands,
};

const PAGE_SIZE = 10;

const EMPTY_PAGINATION = {
  records: [],
  page: 1,
  recordsPerPage: PAGE_SIZE,
  totalRecords: 0,
};

const DEFAULT_TABLE_FILTER = {
  ascendingOrder: false,
  page: 1,
  sortField: 'createDate',
};

const UniversalEinListing = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const match = useRouteMatch();

  const classes = useStyles();
  const UniversalEin = params.id;
  const [einIssuingCountry, ein] = UniversalEin.split('_');
  const [goBackUrl] = useState(() => location.state?.goBackPage || '');
  const paths = match.path.split('/');
  if (paths.length === 3) {
    history.replace({
      pathname: `/universal-ein/${UniversalEin}/campaigns`,
      state: { goBackPage: goBackUrl },
    });
  }

  const initFilter = {
    ...DEFAULT_TABLE_FILTER,
    ein,
    einIssuingCountry,
    recordsPerPage: PAGE_SIZE,
  };

  const initFetchFlagRef = useRef({
    campaign: false,
    brand: false,
  });

  const activeTab = paths.length > 3 ? paths[3] : TabName.CAMPAIGN;
  const headRows =
    activeTab === TabName.CAMPAIGN ? CampaignHeadRows : BrandHeadRows;

  const [loader, setLoader] = useState(true);
  const [universalEinSuspended, setUniversalEinSuspended] = useState(
    location.state?.suspended ?? null
  );
  const [effectedCampaignCount, setEffectedCampaignCount] = useState(0);
  const [suspensionRuleId, setSuspensionRuleId] = useState(null);
  const [openSuspensionModal, setOpenSuspensionModal] = useState(false);
  const [progressMsg, setProgressMsg] = useState(null);

  const campaignTable = useTable(
    async (filter) => {
      // no need to query the inactive tab data when URL changed, but init fetch is needed to display count
      if (activeTab !== TabName.CAMPAIGN && initFetchFlagRef.current.campaign) {
        return {
          data: campaignTable.tableData,
          page: filter.page,
          total: campaignTable.total,
          rowsPerPage: campaignTable.pagination.rowsPerPage,
        };
      }
      const result = await fetchTableData(TabName.CAMPAIGN, filter);
      initFetchFlagRef.current.campaign = true;

      return result;
    },
    {
      deps: [location.search, activeTab],
      defaultFilter: DEFAULT_TABLE_FILTER,
    }
  );

  const brandTable = useTable(
    async (filter) => {
      // no need to query the inactive tab data when URL changed, but init fetch is needed to display count
      if (activeTab !== TabName.BRAND && initFetchFlagRef.current.brand) {
        return {
          data: brandTable.tableData,
          page: filter.page,
          total: brandTable.total,
          rowsPerPage: brandTable.pagination.rowsPerPage,
        };
      }
      const result = await fetchTableData(TabName.BRAND, filter);
      initFetchFlagRef.current.brand = true;
      return result;
    },
    {
      deps: [location.search, activeTab],
      defaultFilter: DEFAULT_TABLE_FILTER,
    }
  );

  const handleChangeTab = (event, newTabName) => {
    event.preventDefault();
    history.push({
      pathname: `/universal-ein/${UniversalEin}/${newTabName}`,
      state: { goBackPage: goBackUrl },
    });
  };

  const fetchTableData = async (tab, filter) => {
    const query = {
      ...filter,
      ein,
      einIssuingCountry,
      channelId: tab === TabName.CAMPAIGN ? '10DLC' : undefined,
      recordsPerPage: PAGE_SIZE,
    };
    const apiFn = LIST_API[tab];
    const result = await apiFn(query);
    return {
      data: result?.records || EMPTY_PAGINATION.data,
      page: result?.page || EMPTY_PAGINATION.page,
      rowsPerPage: result?.recordsPerPage || EMPTY_PAGINATION.recordsPerPage,
      total: result?.totalRecords || EMPTY_PAGINATION.totalRecords,
    };
  };

  const displayTable = (() => {
    if (activeTab === TabName.CAMPAIGN) {
      return campaignTable;
    } else if (activeTab === TabName.BRAND) {
      return brandTable;
    }
    return EMPTY_PAGINATION;
  })();

  const suspendBtnContent = () => {
    const actionText = universalEinSuspended
      ? 'Lift Universal EIN Suspension'
      : 'Suspend Universal EIN';
    return (
      <React.Fragment>
        <FontAwesomeIcon
          icon={universalEinSuspended ? faRotateRight : faSquareMinus}
          size="lg"
        />
        <span>{actionText}</span>
      </React.Fragment>
    );
  };

  const handleModalSubmit = (payload) => {
    let authUser = null;
    const user = localStorage.getItem('user');
    if (user && JSON.parse(user)) {
      authUser = JSON.parse(user).profile.mnoNetworkId;
    }
    const data = {
      ...payload, // explanation, suspensionCategory
      authUser,
      ein,
      einIssuingCountry,
      status: universalEinSuspended ? 'INACTIVE' : 'ACTIVE',
    };
    createUniversalEinSuspensionRule(data);
  };

  const createUniversalEinSuspensionRule = async (payload) => {
    try {
      setProgressMsg(
        `${suspensionRuleId ? 'Update' : 'Create'} suspension rule`
      );

      if (suspensionRuleId) {
        await updateSuspensionRule(suspensionRuleId, payload);
      } else {
        await createSuspensionRule(payload);
      }

      setTimeout(() => {
        setProgressMsg(null);
        history.push({ pathname: goBackUrl });
        const message = universalEinSuspended
          ? 'Brand Suspension Successfully Lifted'
          : 'Brand Successfully Suspended';
        toastFlashMessage(message, 'success');
      }, 1000);
    } catch (e) {
      setProgressMsg(null);
      history.push({ pathname: goBackUrl });
    }
  };

  const checkSuspensionStatus = async () => {
    if (universalEinSuspended === null || suspensionRuleId === null) {
      const rules = await getSuspensionRule({
        brandUniversalEin: UniversalEin,
        exactMatch: true,
      });
      if (rules && rules.records.length > 0) {
        const suspensionRule = rules.records.find(
          (rule) =>
            rule.ein === ein && rule.einIssuingCountry === einIssuingCountry
        );
        if (suspensionRule) {
          setSuspensionRuleId(suspensionRule.ruleId);
        }
        const result = suspensionRule && suspensionRule.status === 'ACTIVE';
        setUniversalEinSuspended(result);
      } else {
        setUniversalEinSuspended(false);
      }
    }
    setLoader(false);
  };

  const getEffectedCampaignCount = async () => {
    if (universalEinSuspended === null) return;
    const queryEffect = {
      ...initFilter,
      channelId: '10DLC',
      mnoStatus: universalEinSuspended
        ? MNO_STATUS_ENUM.SUSPENDED
        : MNO_STATUS_ENUM.REGISTERED,
    };
    const effectedCampaign = await getSuspensionPreviewCampaigns(queryEffect);
    if (effectedCampaign.totalRecords > 0) {
      setEffectedCampaignCount(effectedCampaign.totalRecords);
    }
  };

  useEffect(() => {
    checkSuspensionStatus();
  }, []);

  useEffect(() => {
    getEffectedCampaignCount();
  }, [universalEinSuspended]);

  return (
    <section data-testid="universalEinListing" className="page-content">
      {loader ? (
        <Loader />
      ) : (
        <Container maxWidth={false} className={classes.root}>
          <Grid container className="top-blk">
            <Grid item xs={6}>
              <Link
                data-testid="universalEinListingBackButton"
                className={`paragraph ${classes.backLink}`}
                to="#"
                onClick={(event) => {
                  event.preventDefault();
                  if (goBackUrl !== '') {
                    history.push(goBackUrl);
                  } else {
                    history.goBack();
                  }
                }}
              >
                <Flex sx={{ alignItems: 'center' }}>
                  <Box sx={{ mr: 's' }}>
                    <FontAwesomeIcon icon={faAngleLeft} size="lg" />
                  </Box>
                  back
                </Flex>
              </Link>
            </Grid>
            <Grid item xs={6} className="button-wrapper flex-centered">
              <Button
                color={universalEinSuspended ? 'secondary' : 'primary'}
                onClick={() => setOpenSuspensionModal(true)}
                data-testid={
                  universalEinSuspended
                    ? 'liftUniversalEINSuspensionButton'
                    : 'suspendUniversalEINButton'
                }
              >
                {suspendBtnContent()}
              </Button>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <div className={classes.title}>
                <h3 className="heading1">Universal EIN: {UniversalEin}</h3>
                <p className="heading2">
                  <span>Universal EIN Suspended: </span>
                  {universalEinSuspended ? (
                    <React.Fragment>
                      <FontAwesomeIcon
                        icon={faSquareMinusSolid}
                        size="xl"
                        style={{ color: 'D41C54' }}
                      />{' '}
                      <span>YES</span>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <FontAwesomeIcon
                        icon={faSquareCheckSolid}
                        size="xl"
                        style={{ color: '009B7C' }}
                      />{' '}
                      <span>NO</span>
                    </React.Fragment>
                  )}
                </p>
              </div>
            </Grid>
          </Grid>
          <Grid container xs={12}>
            <Grid item xs={12}>
              <div className={classes.tabs}>
                <div
                  onClick={(event) => handleChangeTab(event, TabName.CAMPAIGN)}
                  className={`tab-label ${
                    activeTab === TabName.CAMPAIGN ? 'active' : ''
                  }`}
                >
                  <Box sx={{ float: 'left', mr: 'xs' }}>
                    <FontAwesomeIcon icon={faBullhorn} />
                  </Box>
                  {campaignTable.total} Campaigns
                </div>
                <div
                  data-testid="universalEinListingBrandChangeButton"
                  onClick={(event) => handleChangeTab(event, TabName.BRAND)}
                  className={`tab-label ${
                    activeTab === TabName.BRAND ? 'active' : ''
                  }`}
                >
                  <Box sx={{ float: 'left', mr: 'xs' }}>
                    <FontAwesomeIcon icon={faTag} />
                  </Box>
                  <span>{brandTable.total} Brands</span>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} className={classes.tabContents}>
              <Table
                loading={displayTable.loading}
                emptyState={`no ${activeTab} to view`}
                handleChangePage={displayTable.handleChangePage}
                createSortHandler={displayTable.handleSorting}
                filter={displayTable.filter}
                headRows={headRows}
                tableData={displayTable.tableData.map((record) => {
                  if (activeTab === TabName.BRAND) {
                    return <MatchedBrandRow data={record} key={record.uid} />;
                  }
                  return <MatchedCampaignRow data={record} key={record.uid} />;
                })}
                pagination={displayTable.pagination}
              />
            </Grid>
          </Grid>
        </Container>
      )}
      <Backdrop
        className={classes.progressBackground}
        open={progressMsg !== null}
        data-testid="universalEinListingBackdrop"
      >
        <Container className={classes.progressModal}>
          <Spinner />
          <Typography variant="body1" className={classes.progressText}>
            {progressMsg}
          </Typography>
        </Container>
      </Backdrop>
      <SuspensionModal
        open={openSuspensionModal}
        handleClose={() => setOpenSuspensionModal(false)}
        handleSubmit={handleModalSubmit}
        universalEin={UniversalEin}
        totalCampaigns={effectedCampaignCount}
        totalBrands={brandTable.total}
        isSuspended={universalEinSuspended}
      />
    </section>
  );
};

export default UniversalEinListing;
